import {
  CURRENT_CONTENT_SCHEMA_VERSION,
  classifyContentSchemaVersion,
  type KnowledgeItem,
} from "@note-app/contracts";
import {
  migrateCanvasToCurrent,
  migrateDocumentToCurrent,
  migrateFolderToCurrent,
  migrateKnowledgeItemToCurrent,
  migrateRevisionRecordToCurrent,
  migrateTagToCurrent,
  type Folder,
  type RevisionRecord,
  type Tag,
} from "@note-app/domain";
import {
  NotebundleService,
  type ArchiveEntry,
  type ArchivePort,
  type ValidatedBundleEntry,
  type ValidatedNotebundle,
} from "@note-app/import-export";
import { RepositoryAuthoritativeDataSource } from "@note-app/import-export/host";
import type {
  AttachmentRecord,
  BlobStore,
  HashPort,
  IncrementalHash,
} from "@note-app/knowledge-services";
import {
  ALL_KNOWLEDGE_STORES,
  type IndexedDbKnowledgeStore,
} from "@note-app/storage-indexeddb";
import { unzipSync, zipSync } from "fflate";

import { PlatformRuntimeError } from "./capabilities.ts";
import type {
  BundleExport,
  BundleImportResult,
  KnowledgeBundlePort,
} from "./ports.ts";

const MAX_IMPORT_BYTES = 512 * 1024 * 1024;

export class BrowserKnowledgeBundlePort implements KnowledgeBundlePort {
  private readonly service: NotebundleService;

  public constructor(
    private readonly store: IndexedDbKnowledgeStore,
    private readonly blobs: BlobStore,
    private readonly now: () => number = Date.now,
  ) {
    this.service = new NotebundleService(
      new FflateArchivePort(),
      new BrowserHashPort(),
      { next: () => crypto.randomUUID() },
    );
  }

  async exportBundle(signal?: AbortSignal): Promise<BundleExport> {
    const source = new RepositoryAuthoritativeDataSource({
      knowledgeBaseId: await this.store.knowledgeBaseId(),
      repositories: this.store.repositories,
      attachments: this.store.attachments,
      blobs: this.blobs,
      settings: {
        product: "丰月笔记 / FengYue Notes",
        platform: "browser",
      },
    });
    const bundle = await this.service.export(
      source,
      this.now(),
      signal === undefined ? undefined : { signal },
    );
    return {
      bytes: bundle.bytes,
      fileName: `fengyue-notes-${new Date(this.now()).toISOString().slice(0, 10)}.notebundle`,
      entryCount: bundle.entryCount,
      sha256: bundle.sha256,
    };
  }

  async downloadBundle(bundle: BundleExport): Promise<void> {
    const url = URL.createObjectURL(
      new Blob([new Uint8Array(bundle.bytes).buffer], {
        type: "application/vnd.fengyue.notebundle+zip",
      }),
    );
    try {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = bundle.fileName;
      anchor.rel = "noopener";
      anchor.click();
    } finally {
      queueMicrotask(() => URL.revokeObjectURL(url));
    }
  }

  async importBundle(
    file: Blob,
    signal?: AbortSignal,
  ): Promise<BundleImportResult> {
    if (file.size > MAX_IMPORT_BYTES) {
      throw new PlatformRuntimeError(
        "operation-failed",
        "This notebundle exceeds the 512 MiB safe browser import limit.",
        "files",
      );
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    const validated = await this.service.validate(
      bytes,
      signal === undefined ? undefined : { signal },
    );
    return restoreValidatedBundle(this.store, this.blobs, validated);
  }
}

class FflateArchivePort implements ArchivePort {
  async pack(entries: readonly ArchiveEntry[]): Promise<Uint8Array> {
    const input: Record<string, Uint8Array> = {};
    for (const entry of entries) input[entry.path] = entry.bytes;
    return zipSync(input, { level: 0 });
  }

  async unpack(archive: Uint8Array): Promise<readonly ArchiveEntry[]> {
    const unzipped = unzipSync(archive);
    return Object.entries(unzipped).map(([path, bytes]) => ({ path, bytes }));
  }
}

class BrowserHashPort implements HashPort {
  createSha256(): IncrementalHash {
    throw new PlatformRuntimeError(
      "unsupported",
      "Streaming hashing is not available in this browser adapter.",
      "files",
    );
  }

  async sha256(bytes: Uint8Array): Promise<string> {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new Uint8Array(bytes).buffer,
    );
    return [...new Uint8Array(digest)]
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }
}

async function restoreValidatedBundle(
  store: IndexedDbKnowledgeStore,
  blobs: BlobStore,
  bundle: ValidatedNotebundle,
): Promise<BundleImportResult> {
  store.assertWritable();
  const parsed = parseEntries(bundle.entries);
  const attachmentReferenceCounts = restoredAttachmentReferenceCounts(
    parsed.documents.values(),
    parsed.canvases.values(),
  );
  for (const attachment of parsed.attachments) {
    const bytes = parsed.blobs.get(attachment.storageKey);
    if (!bytes) {
      throw new PlatformRuntimeError(
        "corrupt-data",
        `Attachment ${attachment.id} has no matching blob.`,
        "files",
      );
    }
    const temp = await blobs.createTemp();
    await blobs.appendTemp(temp, bytes);
    await blobs.commitTemp(temp, attachment.storageKey);
  }

  const database = store.database();
  const tx = database.transaction([...ALL_KNOWLEDGE_STORES], "readwrite", {
    durability: "strict",
  });
  try {
    await Promise.all(
      ALL_KNOWLEDGE_STORES
        .filter((name) => name !== "meta" && name !== "blobs")
        .map((name) => tx.objectStore(name).clear()),
    );
    const restoredAt = Date.now();
    await tx.objectStore("meta").put({
      key: "knowledgeBaseId",
      value: crypto.randomUUID(),
    });
    await tx.objectStore("meta").put({
      key: "restoredFrom",
      value: {
        knowledgeBaseId: bundle.manifest.sourceKnowledgeBaseId,
        bundleSha256: bundle.bundleSha256,
        restoredAt,
      },
    });
    for (const folder of parsed.folders) {
      await tx.objectStore("folders").put(folder);
    }
    for (const tag of parsed.tags) await tx.objectStore("tags").put(tag);
    for (const item of parsed.items) await tx.objectStore("items").put(item);
    for (const item of parsed.items) {
      if (item.kind === "document") {
        const document = parsed.documents.get(item.id);
        if (document) {
          await tx.objectStore("documents").put({
            itemId: item.id,
            revision: item.revision,
            contentRevision: item.revision,
            document,
          });
        }
      } else {
        const canvas = parsed.canvases.get(item.id);
        if (canvas) {
          await tx.objectStore("canvases").put({
            itemId: item.id,
            revision: item.revision,
            contentRevision: item.revision,
            canvas,
          });
        }
      }
    }
    for (const revision of parsed.revisions) {
      await tx.objectStore("revisions").put(revision);
    }
    for (const attachment of parsed.attachments) {
      const referenceCount = attachmentReferenceCounts.get(attachment.id) ?? 0;
      const { unreferencedAt: _previous, ...required } = attachment;
      await tx.objectStore("attachments").put({
        ...required,
        referenceCount,
        ...(referenceCount === 0 ? { unreferencedAt: restoredAt } : {}),
      });
    }
    for (const item of parsed.items) {
      await tx.objectStore("changes").add({
        entityId: item.id,
        entityType: "item",
        operation: "create",
        revision: item.revision,
        committedAt: restoredAt,
        payload: { restored: true },
      });
    }
    await tx.done;
    return {
      items: parsed.items.length,
      attachments: parsed.attachments.length,
      sourceKnowledgeBaseId: bundle.manifest.sourceKnowledgeBaseId,
    };
  } catch (error) {
    try {
      tx.abort();
    } catch {
      // The browser may already have aborted.
    }
    throw new PlatformRuntimeError(
      "operation-failed",
      "The validated bundle could not be restored; the previous workspace remains intact.",
      "files",
      { cause: error },
    );
  }
}

function parseEntries(entries: readonly ValidatedBundleEntry[]) {
  const items: KnowledgeItem[] = [];
  const folders: Folder[] = [];
  const tags: Tag[] = [];
  const revisions: RevisionRecord[] = [];
  const documents = new Map<string, import("@note-app/contracts").DomainDocument>();
  const canvases = new Map<string, import("@note-app/contracts").DomainCanvas>();
  const attachments: AttachmentRecord[] = [];
  const blobs = new Map<string, Uint8Array>();
  for (const entry of entries) {
    if (entry.kind === "attachment-blob") {
      blobs.set(entry.path.slice("attachments/".length), entry.bytes);
      continue;
    }
    if (entry.kind === "settings") continue;
    const value = JSON.parse(new TextDecoder().decode(entry.bytes)) as unknown;
    switch (entry.kind) {
      case "knowledge-item": {
        const item = migrateKnowledgeItemToCurrent(value as KnowledgeItem).value;
        items.push(item);
        break;
      }
      case "document": {
        const document = migrateDocumentToCurrent(
          value as import("@note-app/contracts").DomainDocument,
        ).value;
        documents.set(pathId(entry.path), document);
        break;
      }
      case "canvas": {
        const canvas = migrateCanvasToCurrent(
          value as import("@note-app/contracts").DomainCanvas,
        ).value;
        canvases.set(pathId(entry.path), canvas);
        break;
      }
      case "folder": {
        const folder = migrateFolderToCurrent(value as Folder).value;
        folders.push(folder);
        break;
      }
      case "tag": {
        const tag = migrateTagToCurrent(value as Tag).value;
        tags.push(tag);
        break;
      }
      case "revision": {
        const revision = migrateRevisionRecordToCurrent(
          value as RevisionRecord,
        ).value;
        revisions.push(revision);
        break;
      }
      case "attachment-metadata":
        attachments.push(parseAttachment(value));
        break;
    }
  }
  return { items, folders, tags, revisions, documents, canvases, attachments, blobs };
}

function pathId(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1, -".json".length);
}

function parseAttachment(value: unknown): AttachmentRecord {
  if (
    typeof value !== "object" ||
    value === null ||
    !("id" in value) ||
    typeof value.id !== "string" ||
    !("sha256" in value) ||
    typeof value.sha256 !== "string" ||
    !("storageKey" in value) ||
    typeof value.storageKey !== "string"
  ) {
    throw new PlatformRuntimeError(
      "corrupt-data",
      "The notebundle contains invalid attachment metadata.",
      "files",
    );
  }
  const schemaVersion = "schemaVersion" in value
    ? value.schemaVersion
    : undefined;
  if (typeof schemaVersion !== "number") {
    throw new PlatformRuntimeError(
      "corrupt-data",
      "The notebundle contains attachment metadata without a schema version.",
      "files",
    );
  }
  const compatibility = classifyContentSchemaVersion(schemaVersion);
  if (compatibility === "future" || compatibility === "unsupported-old") {
    throw new PlatformRuntimeError(
      "corrupt-data",
      `Attachment metadata uses unsupported schema ${schemaVersion}.`,
      "files",
    );
  }
  return {
    ...(value as AttachmentRecord),
    schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
    attachmentId: value.id,
    referenceCount: 0,
    unreferencedAt: Date.now(),
  };
}

function restoredAttachmentReferenceCounts(
  documents: Iterable<import("@note-app/contracts").DomainDocument>,
  canvases: Iterable<import("@note-app/contracts").DomainCanvas>,
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  const add = (attachmentId: string): void => {
    if (!attachmentId) return;
    counts.set(attachmentId, (counts.get(attachmentId) ?? 0) + 1);
  };
  const visit = (candidate: unknown): void => {
    if (typeof candidate === "string") {
      if (!candidate.startsWith("attachment:")) return;
      const encoded = candidate.slice("attachment:".length).replace(/^\/\//, "");
      try {
        add(decodeURIComponent(encoded));
      } catch {
        // Invalid references remain content-validation concerns.
      }
      return;
    }
    if (Array.isArray(candidate)) {
      for (const value of candidate) visit(value);
      return;
    }
    if (typeof candidate !== "object" || candidate === null) return;
    for (const [key, value] of Object.entries(candidate)) {
      if (key === "attachmentId" && typeof value === "string") add(value);
      else visit(value);
    }
  };
  for (const document of documents) visit(document);
  for (const canvas of canvases) visit(canvas);
  return counts;
}
